import React, {
  createContext,
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useRef,
} from "react";

export interface IDescriptionDirtyContext {
  isDirtyRef: MutableRefObject<boolean>;
  setDirty: (dirty: boolean) => void;
}

// Default: no provider mounted (e.g. `DescriptionScreen`'s own unit test,
// which renders it standalone) means writes are a no-op, not a crash.
const DescriptionDirtyContext = createContext<IDescriptionDirtyContext>({
  isDirtyRef: { current: false },
  setDirty: () => undefined,
});

export const useDescriptionDirty = (): IDescriptionDirtyContext =>
  useContext(DescriptionDirtyContext);

/**
 * Whether the entry screen (`DescriptionScreen`) has anything worth losing —
 * description text or a photo — read by `useFlowExitGuard` so it can skip the
 * "Leaving already?" modal while the screen is still empty (RT-4321).
 *
 * A ref rather than state: the guard only needs the latest value at the
 * moment of a click, so writing it on every keystroke should not re-render
 * the chrome. Mounted once around the whole flow, alongside
 * `DiscardConfirmationProvider`, so it survives whichever screen is on show.
 */
export const DescriptionDirtyProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const isDirtyRef = useRef(false);
  const setDirty = useCallback((dirty: boolean) => {
    isDirtyRef.current = dirty;
  }, []);

  return (
    <DescriptionDirtyContext.Provider value={{ isDirtyRef, setDirty }}>
      {children}
    </DescriptionDirtyContext.Provider>
  );
};
